import { apiClient, API_BASE_URL } from './client'
import { ApiError } from './errors'
import type {
  DocumentCounts,
  DocumentResultField,
  DocumentVariant,
  EvaluationDocument,
  EvaluationStatus,
  JobStatus,
  RecognitionArm,
} from '@/types/domain'

const http = () => apiClient.axios

/**
 * What `POST …/documents/:documentId/reviews` answers — snake_case, unlike
 * the review stored inside the document's `result`, which is camelCase
 * (`FieldReview` in `@/types/domain` models that one). The two describe the
 * same event in two spellings; this is the receipt, not the stored record.
 */
export interface ReviewReceipt {
  fieldId: string
  documentId: string
  accepted: boolean
  correction: string
  /**
   * The value that now stands: the correction on a rejection, and on an
   * acceptance the **delivered** value — which is an earlier correction where
   * the field has one, not the recognizer's reading. Confirming a field
   * someone had corrected used to revert it to the OCR text, silently; it no
   * longer does, and a screen may show this receipt as the value.
   */
  finalText: string
  reviewedBy: string
  reviewedByName?: string
  /** RFC3339. */
  reviewedAt?: string
}

/** One document's record and field map as one arm produced them —
 *  `GET /v1/templates/:id/variant-records?arm=`. */
export interface VariantRecord {
  documentId: string
  title?: string
  /** How many pages the upload had, as stored on the document. */
  page?: number
  matchedPage?: number | null
  record?: Record<string, unknown>
  /** The arm's field map, in the same shape as a document's own `result`, so
   *  a records table swaps arms by swapping this map and nothing else. */
  result?: Record<string, DocumentResultField>
  needsReview?: boolean
  needsReviewCount: number
  /** Which model produced this row. Empty for `ocr` and `human`, which run
   *  none; with no `model=` on the request, the rows can be from different
   *  models — the newest reading per document wins. */
  model?: string
}

/** What `POST …/documents/:documentId/move` answers. The analysis is queued
 *  as part of the move unless it was declined, and `analysisQueued: false`
 *  means the move stood but the job could not be enqueued — the caller can
 *  start it again. */
export interface MoveResult {
  document?: EvaluationDocument
  analysisQueued?: boolean
  jobId?: string
}

export interface DocumentPage {
  docs: EvaluationDocument[]
  total: number
  page: number
  pageSize: number
  nextCursor: string | null
}

/**
 * The per-template evaluation-document endpoints. Ported from
 * `lib/services/api/eval_documents_api.dart` (280 lines).
 *
 * The listing renames two fields on the wire — `filename` for the title and
 * `status` for the evaluation status — which is why the response goes
 * through `parsePage` rather than straight into the model.
 */
function parsePage(
  data: unknown,
  requestedPage: number,
  requestedPageSize: number,
): DocumentPage {
  const map = (typeof data === 'object' && data !== null ? data : {}) as Record<string, unknown>
  const raw = (map.documents ?? map.items ?? []) as Array<Record<string, unknown>>
  const docs = raw.map((item) => {
    const doc = { ...item } as Record<string, unknown>
    doc.title ??= doc.filename
    doc.evaluationStatus ??= doc.status
    return doc as unknown as EvaluationDocument
  })
  const total = (map.total as number) ?? docs.length
  const page = (map.page as number) ?? requestedPage
  const pageSize = (map.page_size as number) ?? requestedPageSize
  const nextCursor =
    (map.next_cursor as string | undefined) ??
    (pageSize > 0 && page * pageSize < total ? String(page + 1) : null)
  return { docs, total, page, pageSize, nextCursor }
}

/**
 * The count endpoint answers `{count, counts}`, and `counts` is **flat**: one
 * key per evaluation status beside `total`, `needsReview`, `needsReviewFields`
 * and `byMatchedPage`. Nothing on the wire is called `byStatus` — that is the
 * client's own grouping, and building it here is what lets a screen ask for
 * `byStatus.noMatch` without knowing which keys are statuses and which are
 * aggregates.
 *
 * `needsReviewFields` counts flagged **fields** across the flagged documents,
 * so "Unklar" can be stated in fields and documents at once. The gateway omits
 * it when its aggregate query fails, and it stays undefined here rather than
 * becoming 0: "not counted" is not "none".
 */
const COUNT_AGGREGATE_KEYS = new Set([
  'total',
  'needsReview',
  'needsReviewFields',
  'byMatchedPage',
])

export function parseCounts(data: unknown): DocumentCounts {
  const body = (typeof data === 'object' && data !== null ? data : {}) as Record<string, unknown>
  const raw = (typeof body.counts === 'object' && body.counts !== null
    ? body.counts
    : {}) as Record<string, unknown>

  const byStatus: Partial<Record<EvaluationStatus, number>> = {}
  let summed = 0
  for (const [key, value] of Object.entries(raw)) {
    if (COUNT_AGGREGATE_KEYS.has(key) || typeof value !== 'number') continue
    byStatus[key as EvaluationStatus] = value
    summed += value
  }

  return {
    total:
      typeof raw.total === 'number'
        ? raw.total
        : typeof body.count === 'number'
          ? body.count
          : summed,
    byStatus,
    ...(typeof raw.needsReview === 'number' ? { needsReview: raw.needsReview } : {}),
    ...(typeof raw.needsReviewFields === 'number'
      ? { needsReviewFields: raw.needsReviewFields }
      : {}),
    ...(typeof raw.byMatchedPage === 'object' && raw.byMatchedPage !== null
      ? { byMatchedPage: raw.byMatchedPage as Record<string, number> }
      : {}),
  }
}

export const documentsApi = {
  async list(
    templateId: string,
    options: {
      status?: EvaluationStatus
      needsReview?: boolean
      page?: number
      pageSize?: number
      sortBy?: string
      sortOrder?: string
    } = {},
  ): Promise<DocumentPage> {
    const page = options.page ?? 1
    const pageSize = options.pageSize ?? 25
    const { data } = await http().get(`/v1/templates/${templateId}/documents`, {
      params: {
        ...(options.status ? { status: options.status } : {}),
        ...(options.needsReview ? { needs_review: true } : {}),
        page,
        page_size: pageSize,
        ...(options.sortBy ? { sort_by: options.sortBy } : {}),
        ...(options.sortOrder ? { sort_order: options.sortOrder } : {}),
      },
    })
    return parsePage(data, page, pageSize)
  },

  async counts(templateId: string): Promise<DocumentCounts> {
    const { data } = await http().get<unknown>(`/v1/templates/${templateId}/documents/count`)
    return parseCounts(data)
  },

  /** The rendered pages of one uploaded document. */
  async pages(templateId: string, documentId: string): Promise<EvaluationDocument[]> {
    const { data } = await http().get(`/v1/templates/${templateId}/documents`, {
      params: { originalDocumentId: documentId },
    })
    return parsePage(data, 1, 0).docs
  },

  /**
   * Uploads one scan. The endpoint takes a single file under the form field
   * `file` and answers `{id}` — it does not queue an analysis, which is a
   * separate `startAnalysis` call.
   */
  async upload(
    templateId: string,
    file: File | Blob,
    filename: string,
    onProgress?: (sent: number, total: number) => void,
  ): Promise<string> {
    const form = new FormData()
    form.append('file', file, filename)
    const { data } = await http().post<{ id: string }>(
      `/v1/templates/${templateId}/documents`,
      form,
      { onUploadProgress: (event) => onProgress?.(event.loaded, event.total ?? 0) },
    )
    return data.id
  },

  /**
   * Uploads several scans one after another, reporting each as it lands.
   * Sequential on purpose: the gateway caps concurrent uploads per instance
   * and answers 503 ("Too many concurrent uploads") past it, so firing a
   * whole drop of files at once loses some of them.
   */
  async uploadAll(
    templateId: string,
    files: Array<{ file: File | Blob; filename: string }>,
    onEach?: (done: number, total: number, filename: string) => void,
  ): Promise<string[]> {
    const ids: string[] = []
    for (const [index, entry] of files.entries()) {
      ids.push(await documentsApi.upload(templateId, entry.file, entry.filename))
      onEach?.(index + 1, files.length, entry.filename)
    }
    return ids
  },

  async patch(
    templateId: string,
    documentId: string,
    updates: Record<string, unknown>,
  ): Promise<void> {
    await http().patch(`/v1/templates/${templateId}/documents/${documentId}`, updates)
  },

  async remove(templateId: string, documentId: string): Promise<void> {
    await http().delete(`/v1/templates/${templateId}/documents/${documentId}`)
  },

  async batchDelete(templateId: string, documentIds: string[]): Promise<number> {
    const { data } = await http().post<{ deleted_count?: number }>(
      `/v1/templates/${templateId}/documents/batch-delete`,
      { document_ids: documentIds },
    )
    return data.deleted_count ?? 0
  },

  /** Re-parents a document. The old analysis is dropped — its result map was
   *  keyed by the old template's boxes — and the document is queued against
   *  its new template unless `analyze` is false. */
  async move(
    templateId: string,
    documentId: string,
    targetTemplateId: string,
    options: { analyze?: boolean } = {},
  ): Promise<MoveResult> {
    const { data } = await http().post<MoveResult>(
      `/v1/templates/${templateId}/documents/${documentId}/move`,
      {
        targetTemplateId,
        analyze: options.analyze ?? true,
      },
    )
    return data
  },

  /**
   * Queues an analysis.
   *
   * `forcePage` pins the job to one template layout instead of letting
   * matching pick. `corners` additionally hands the worker the four corners
   * of the form on the scan — **fractions** of its width and height, in the
   * order top-left, top-right, bottom-right, bottom-left. Fractions travel
   * across every scale, so they must not be converted to pixels on the way
   * out. They apply to exactly one document and need a `forcePage` unless
   * the template has a single page.
   *
   * `arm` asks for one recognition arm instead of an ordinary analysis
   * ('llm_text' or 'llm_image'); the reading is stored beside the OCR
   * baseline rather than replacing it.
   */
  async startAnalysis(
    templateId: string,
    documentIds: string[],
    options: {
      forcePage?: number
      corners?: number[][]
      arm?: string
      model?: string
      apply?: boolean
    } = {},
  ): Promise<string> {
    const { data } = await http().post<{ job_id?: string }>(`/v1/templates/${templateId}/jobs`, {
      documentIds,
      ...(options.forcePage != null ? { forcePage: options.forcePage } : {}),
      ...(options.corners ? { manualCorners: options.corners } : {}),
      ...(options.arm ? { arm: options.arm } : {}),
      ...(options.model ? { model: options.model } : {}),
      ...(options.apply != null ? { apply: options.apply } : {}),
    })
    return data.job_id ?? ''
  },

  async jobStatus(jobId: string): Promise<JobStatus> {
    const { data } = await http().get<JobStatus>(`/v1/jobs/${jobId}`)
    return data
  },

  /**
   * Records one person's judgement of one field.
   *
   * `accepted: true` with no correction is the ordinary case and means more
   * than "nothing happened": the reviewer signs off on the **delivered**
   * value — the correction where the field already has one, the recognizer's
   * text otherwise — the gateway writes that into `finalText`, and the field
   * counts as reviewed from then on, which is what takes it out of the review
   * queue. A submit that changed nothing is therefore a real call, not one to
   * skip. (Accepting used to write the OCR text back, quietly undoing an
   * earlier correction; it no longer does.)
   *
   * A rejection must carry the value the field should have had — but an
   * **empty** one is a value: "this field is blank, whatever the recognizer
   * read". The gateway stores the empty `finalText` and the field then
   * delivers nothing rather than the OCR text, so a review screen can offer
   * "Feld ist leer". Only a *missing* correction is refused, here and at the
   * gateway (400), because a rejection with nothing in it would leave the
   * wrong value standing while claiming the field was checked.
   *
   * The distinction is carried by `undefined` vs `''`, so the key is sent
   * whenever it is not undefined: `null` would reach the gateway as absent
   * and be refused.
   *
   * The review is appended to the field's `reviews`; `text` is never
   * overwritten, so the raw analyzer output stays inspectable.
   */
  async addReview(
    templateId: string,
    documentId: string,
    review: { fieldId: string; accepted: boolean; correction?: string },
  ): Promise<ReviewReceipt> {
    if (!review.accepted && review.correction == null) {
      throw new ApiError({
        status: 400,
        code: 'correction_required',
        message: 'Für eine Korrektur wird der richtige Wert benötigt.',
      })
    }
    const { data } = await http().post<Record<string, unknown>>(
      `/v1/templates/${templateId}/documents/${documentId}/reviews`,
      {
        field_id: review.fieldId,
        accepted: review.accepted,
        ...(review.correction != null ? { correction: review.correction } : {}),
      },
    )
    return {
      fieldId: (data.field_id as string) ?? review.fieldId,
      documentId: (data.document_id as string) ?? documentId,
      accepted: (data.accepted as boolean) ?? review.accepted,
      correction: (data.correction as string) ?? '',
      finalText: (data.final_text as string) ?? '',
      reviewedBy: (data.reviewed_by as string) ?? '',
      reviewedByName: (data.reviewed_by_name as string) || undefined,
      reviewedAt: (data.reviewed_at as string) || undefined,
    }
  },

  /** Every recognition arm stored for one document, newest first. */
  async variants(templateId: string, documentId: string): Promise<DocumentVariant[]> {
    const { data } = await http().get<{ variants?: DocumentVariant[] }>(
      `/v1/templates/${templateId}/documents/${documentId}/variants`,
    )
    return data.variants ?? []
  },

  /**
   * One arm's record for every document of the template, for a records table
   * showing what that arm made of the same scans.
   *
   * Unlike a job, this accepts any stored arm — `ocr` and `human` included,
   * which no job can run; an unknown one is a 400 `unknown_arm`.
   *
   * `model` narrows to one model's reading. Omitted, every model that ran
   * the arm is considered and the newest reading per document wins — the
   * same rule the promote and the export use, so the table, the file and an
   * adoption all show one reading. (Omitting it used to match only rows with
   * an empty model column, i.e. nothing but the `ocr` and `human` snapshots:
   * every LLM arm came back with zero records. A screen that said "keine
   * Lesungen" for `llm_text` was showing that bug, not an empty result.)
   */
  async variantRecords(
    templateId: string,
    arm: RecognitionArm | string,
    options: { model?: string } = {},
  ): Promise<VariantRecord[]> {
    const { data } = await http().get<{ records?: VariantRecord[] }>(
      `/v1/templates/${templateId}/variant-records`,
      { params: { arm, ...(options.model ? { model: options.model } : {}) } },
    )
    return data.records ?? []
  },

  /**
   * The address of one field's cropped image. Useful as a cache key or for a
   * link; it cannot be put in an `<img src>`, because the route is behind the
   * session and the browser's own image request carries no Authorization
   * header — that fetch answers 401. Use `snippetObjectUrl` for display.
   */
  snippetUrl(
    templateId: string,
    documentId: string,
    fieldId: string,
    options: { source?: string; pad?: number } = {},
  ): string {
    const source = options.source ?? 'enhanced'
    const pad = options.pad ?? 8
    return `${API_BASE_URL}/v1/templates/${templateId}/documents/${documentId}/fields/${fieldId}/snippet?source=${source}&pad=${pad}`
  },

  /** The snippet's bytes, fetched with the token. */
  async snippetBlob(
    templateId: string,
    documentId: string,
    fieldId: string,
    options: { source?: string; pad?: number; signal?: AbortSignal } = {},
  ): Promise<Blob> {
    const { data } = await http().get<Blob>(
      documentsApi.snippetUrl(templateId, documentId, fieldId, options),
      { responseType: 'blob', signal: options.signal },
    )
    return data
  },

  /** The snippet as an object URL, ready for an `<img src>`. The caller
   *  revokes it — mirrors `filesApi.objectUrl`. */
  async snippetObjectUrl(
    templateId: string,
    documentId: string,
    fieldId: string,
    options: { source?: string; pad?: number; signal?: AbortSignal } = {},
  ): Promise<string> {
    return URL.createObjectURL(
      await documentsApi.snippetBlob(templateId, documentId, fieldId, options),
    )
  },
}
